<script lang="ts" setup>
import { ref, onBeforeMount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useUserStore } from '@/store/user'
import { useNotificationsStore } from '@/store/notifications'
import { notificationError } from '@/app/core/util/notificationUtils'
import { useField, useForm } from 'vee-validate'
import { useI18n } from 'vue-i18n'
import { object, string, array, boolean } from 'yup'
import {
  DBtn,
  DTab,
  DTabs,
  DLoading,
  DMultiselect,
  DTextField,
  DToggleField
} from 'front-library'

import EventsSelector from '../components/EventsSelector.vue'

import {
  getCountries,
  getProcessById,
  updateProcess
} from '@/app/core/services/processes'

/* store, router, notifications */
const router = useRouter()
const route = useRoute()
const store = useUserStore()
const notifications = useNotificationsStore()
const { t } = useI18n()

/* Datos formulario */
const process = ref({
  name: '',
  code: '',
  color: '#000000',
  countries: [] as string[],
  apply_calc: false,
  automatic_events: [] as any[]
})

/* Datos Listas */
const countries = ref([] as any)

/* Datos componente Modal y Loading */

const loading = ref(false)
const messageLoading = ref('')

/* Esquema de validacion campos  */
const validationSchema = object().shape({
  name: string()
    .required(t('form.valid.required'))
    .min(1, t('form.valid.minLength', [1]))
    .max(100, t('form.valid.maxLength', [100]))
    .matches(
      /^[A-Za-z0-9À-ÿ\u00f1\u00d1\s-_&()]+$/,
      t('form.valid.alphanumeric')
    ),
  code: string()
    .required(t('form.valid.required'))
    .min(1, t('form.valid.minLength', [1]))
    .max(100, t('form.valid.maxLength', [100]))
    .matches(
      /^[A-Za-z0-9À-ÿ\u00f1\u00d1\s-_&()]+$/,
      t('form.valid.alphanumeric')
    ),
  countries: array()
    .required(t('form.valid.required'))
    .min(1, t('form.valid.required')),
  color: string()
    .matches(/^#[0-9A-F]{6}$/i, t('form.valid.hexColor'))
    .required(t('form.valid.required')),
  apply_calc: boolean(),
  automatic_events: array()
})

/* Uso del metodo de validación, retorno de errores */
const { handleSubmit, errors, setValues } = useForm({
  initialValues: process.value,
  validationSchema
})

/* Definicion de  campos del formulario */
const { value: name } = useField<string>('name')
const { value: code } = useField<string>('code')
const { value: apply_calc } = useField<boolean>('apply_calc')
const { value: countryIds } = useField<string[]>('countries')
const { value: automatic_events } = useField<any[]>('automatic_events')
const { value: color } = useField<string[]>('color')

/* Metodo que valida los campos obligatorios del formulario */
const onSubmit = handleSubmit((values, actions) => {
  update(values)
})

const update = async (values: any) => {
  loading.value = true
  console.log('values', values)
  await updateProcess(values, route.params.id.toString())
    .then(_ => {
      loading.value = false
      notifications.addNotification({
        title: t('processes.notifications.edited'),
        text: t('processes.notifications.edit'),
        type: 'success',
        time: 12000
      })
      back()
    })
    .catch((error: any) => {
      notificationError(error, 'processes', t, notifications, loading)
    })
  loading.value = false
}

/* Volver */
const back = () => {
  router.back()
}
const modelLoaded = ref(false)
onBeforeMount(async () => {
  loading.value = true
  try {
    countries.value = await getCountries()
    if (route.params.id) {
      // messageLoading.value = t('loading.data')
      const data = await getProcessById(route.params.id.toString())            

      setValues({
        name: data.name,
        code: data.code,
        countries: data.countries,
        apply_calc: data.showInCalc,
        automatic_events: data.automatic,
        color: data.color
      })

      modelLoaded.value = true

      // process.value.permissionsIds = permissions
    }
  } catch (error: any) {
    if (error.response.status === 400) {
      notifications.addNotification({
        title: t('notification.notFound'),
        text: t('processes.notifications.initialLoadError'),
        type: 'error',
        time: 10000
      })
      back()
    } else {
      notificationError(error, 'processes', t, notifications, loading)
    }
  }
  loading.value = false
})
</script>
<template>
  <DLoading v-show="loading" :message="messageLoading" />

  <div class="h-full px-5">
    <div class="bg-gray-50 pb-2 sm:pb-4">
      <div class="leading-4 pt-responsive">
        <p class="text-3xl font-semibold text-shadow text-blue-900">
          {{
            route.params.id
              ? t('processes.form.editTitle')
              : t('processes.form.createTitle')
          }}
        </p>
      </div>
    </div>
    <form class="pt-responsive" @submit.prevent="onSubmit">
      <DTabs>
        <template v-slot:default="slotParams">
          <DTab
            :title="t('tabs.data')"
            :selected-title="slotParams?.selectedTitle">
            <div class="grid grid-cols-8 px-6 py-4">
              <div
                class="col-span-8 sm:col-span-3 lg:col-span-2 text-base font-semibold text-gray-700 pb-2 sm:pb-0">
                {{ t('groups.basics.title') }}
              </div>
              <div
                class="col-span-8 sm:col-span-5 lg:col-span-6 grid grid-cols-6 gap-x-6 gap-y-4">
                <DTextField
                  class="col-span-8 sm:col-span-6 lg:col-span-3 xl:col-span-2"
                  :label="t('processes.form.fields.code.label')"
                  :error="errors.code"
                  :placeholder="t('processes.form.fields.code.placeholder')"
                  :required="true"
                  v-model="code" />
                <DTextField
                  class="col-span-8 sm:col-span-6 lg:col-span-3 xl:col-span-2"
                  :label="t('processes.form.fields.name.label')"
                  :error="errors.name"
                  :placeholder="t('processes.form.fields.name.placeholder')"
                  :required="true"
                  v-model="name" />
                <DTextField
                  class="col-span-8 sm:col-span-6 lg:col-span-3 xl:col-span-2"
                  :label="t('processes.form.fields.color.label')"
                  :error="errors.color"
                  :placeholder="t('processes.form.fields.color.placeholder')"
                  :required="true"
                  :type="'color'"
                  :icon="'ColorPickerIcon'"
                  v-model="color" />
                <DMultiselect
                  class="col-span-8 sm:col-span-6 lg:col-span-3 xl:col-span-2"
                  :label="t('processes.form.fields.countries.label')"
                  :error="errors.countries"
                  :required="true"
                  v-model="countryIds"
                  :options="countries"
                  value-key="id"
                  :header-title="t('processes.form.fields.countries.label')" />
                <DToggleField
                  class="col-span-8 sm:col-span-6 lg:col-span-3 xl:col-span-2"
                  :label="t('processes.form.fields.apply_calc.label')"
                  :error="errors.apply_calc"
                  :required="false"
                  v-model="apply_calc" />
              </div>
            </div>
            <div class="grid grid-cols-8 px-6 py-4">
              <div
                class="col-span-8 sm:col-span-3 lg:col-span-2 text-base font-semibold text-gray-700 pb-2 sm:pb-0">
                {{ t('processes.form.groups.events.title') }}
              </div>

              <EventsSelector
                v-if="modelLoaded"
                :label="t('processes.form.fields.automatic_events.label')"
                :error="errors.automatic_events"
                v-model="automatic_events" />
            </div>
          </DTab>
        </template>
      </DTabs>
      <div class="p-5 bg-white shadow-md h-24 rounded-br-md rounded-bl-md">
        <hr class="pb-5" />
        <div class="flex justify-end gap-2 items-center">
          <DBtn
            type="button"
            class="font-semibold py-1 text-base"
            color="secondary"
            @click="back()">
            {{ t('form.button.cancel') }}
          </DBtn>
          <DBtn
            class="font-semibold py-1 text-base"
            color="blue"
            @click="onSubmit">
            {{
              route.params.id ? t('form.button.update') : t('form.button.save')
            }}
          </DBtn>
        </div>
      </div>
    </form>
  </div>
</template>
